import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  catchError,
  finalize,
  forkJoin,
  of,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { Tooltip } from 'primeng/tooltip';
import { DrawingFile } from '../../../models/drawing-file.model';
import { DrawingStorageFile } from '../../../models/storage.model';
import { StorageService } from '../../services/storage.service';
import { DrawingFileService } from '../../services/drawing-file.service';
import { debouncedSearchTerms } from '../../../utils/list-search';

@Component({
  selector: 'app-use-existing-drawing',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    Paginator,
    Tooltip,
  ],
  templateUrl: './use-existing-drawing.component.html',
})
export class UseExistingDrawingComponent implements OnInit, OnDestroy {
  private drawingFileService = inject(DrawingFileService);
  private storageService = inject(StorageService);

  @Input() excludedIds: number[] = [];
  @Input() currentQuoteId: string | number | null = null;
  @Input() currentRevisionNumber: number | null = null;
  @Output() filesSelected = new EventEmitter<DrawingFile[]>();

  storageFiles: DrawingStorageFile[] = [];
  searchQuery = new FormControl('', { nonNullable: true });
  activeSearch = '';
  totalRecords = 0;
  rows = 20;
  first = 0;
  selectedDrawingFileIds = new Set<number>();
  loadingStorage = false;
  loadError: string | null = null;
  loadingSelectedExisting = false;
  storageError: string | null = null;

  private storageRequests = new Subject<void>();
  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      this.storageRequests
        .pipe(
          switchMap(() => {
            this.loadingStorage = true;
            return this.storageService
              .list('drawing', {
                limit: this.rows,
                offset: this.first,
                reusable: true,
                search: this.activeSearch,
              })
              .pipe(catchError(() => of(null)));
          }),
        )
        .subscribe((response) => {
          this.loadingStorage = false;
          this.storageFiles = response?.results ?? [];
          this.totalRecords = response?.count ?? 0;
          this.loadError = response
            ? null
            : 'Failed to load drawings from Storage.';
        }),
    );
    this.subscriptions.add(
      debouncedSearchTerms(this.searchQuery.valueChanges).subscribe(
        (search) => {
          this.activeSearch = search;
          this.first = 0;
          this.loadStorageFiles();
        },
      ),
    );
    this.loadStorageFiles();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get useSelectedLabel(): string {
    const count = this.selectedDrawingFileIds.size;
    return count ? `Use Selected Drawings (${count})` : 'Use Selected Drawings';
  }

  loadStorageFiles(): void {
    this.storageRequests.next();
  }

  refreshStorageFiles(): void {
    this.selectedDrawingFileIds.clear();
    this.loadStorageFiles();
  }

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? this.rows;
    this.loadStorageFiles();
  }

  isExcluded(fileId: number): boolean {
    return this.excludedIds.includes(fileId);
  }

  isUnavailable(drawingFile: DrawingStorageFile): boolean {
    return (
      this.isExcluded(drawingFile.id) ||
      this.currentRevisionLink(drawingFile) !== undefined
    );
  }

  unavailableReason(drawingFile: DrawingStorageFile): string {
    const link = this.currentRevisionLink(drawingFile);
    if (link) {
      return `Already linked to "${link.linked_quote_item_name}" in this quote revision`;
    }
    return this.isExcluded(drawingFile.id)
      ? 'Already used by an item being added to this quote'
      : '';
  }

  private currentRevisionLink(drawingFile: DrawingStorageFile) {
    if (this.currentQuoteId === null) {
      return undefined;
    }
    // Quote ids are typed as strings but arrive from the API as numbers.
    const quoteId = String(this.currentQuoteId);
    return drawingFile.linked_quote_items.find(
      (link) =>
        String(link.quote_id) === quoteId &&
        link.linked_revision_number === this.currentRevisionNumber,
    );
  }

  isSelected(fileId: number): boolean {
    return this.selectedDrawingFileIds.has(fileId);
  }

  toggleExistingDrawingFileSelection(drawingFile: DrawingStorageFile): void {
    const fileId = drawingFile.id;
    if (this.isUnavailable(drawingFile)) {
      return;
    }
    if (this.selectedDrawingFileIds.has(fileId)) {
      this.selectedDrawingFileIds.delete(fileId);
    } else {
      this.selectedDrawingFileIds.add(fileId);
    }
  }

  useSelectedExistingDrawingFiles(): void {
    const selectedIds = Array.from(this.selectedDrawingFileIds);
    if (!selectedIds.length) {
      return;
    }

    this.loadingSelectedExisting = true;
    this.storageError = null;
    const subscription = forkJoin(
      selectedIds.map((id) => this.drawingFileService.getDrawingFile(id)),
    )
      .pipe(finalize(() => (this.loadingSelectedExisting = false)))
      .subscribe({
        next: (files) => {
          if (
            files.some((file) => !file.latest_extraction?.confirmed_dimensions)
          ) {
            this.storageError =
              'Selected drawings must have confirmed dimensions. Upload a new drawing file to review and change its information.';
            return;
          }
          this.filesSelected.emit(files);
        },
        error: () => {
          this.storageError = 'Failed to load selected drawings.';
        },
      });
    this.subscriptions.add(subscription);
  }
}
